const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
});

const COMPACT_UNITS = [
    {threshold: 1_000_000_000, suffix: "B"},
    {threshold: 1_000_000, suffix: "M"},
    {threshold: 1_000, suffix: "K"},
] as const;

// Abbreviates only when the value divides evenly to a single decimal, so 100,001 stays exact.
function toCompactMagnitude(value: number): string | null {
    const magnitude = Math.abs(value);

    for (const {threshold, suffix} of COMPACT_UNITS) {
        if (magnitude < threshold) {
            continue;
        }

        const scaled = magnitude / threshold;
        const rounded = Math.round(scaled * 10) / 10;

        if (Math.abs(scaled - rounded) > 1e-9) {
            continue;
        }

        return `${rounded.toLocaleString("en-US", {maximumFractionDigits: 1})}${suffix}`;
    }

    return null;
}

export function parseNumberRangeValue(values: readonly string[]): [number, number] | null {
    if (values.length !== 2) {
        return null;
    }

    // Number("") is 0, which would render a blank bound as a real one; the slider cannot show an
    // open bound, so reject instead of coercing.
    const min = values[0].trim() === "" ? Number.NaN : Number(values[0]);
    const max = values[1].trim() === "" ? Number.NaN : Number(values[1]);

    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
        return null;
    }

    return [min, max];
}

export function hasNumberRangeValues(values?: readonly string[]): boolean {
    return Array.isArray(values) && parseNumberRangeValue(values) !== null;
}

export function formatNumberRangeBound(
    value: number,
    valueFormat: "currency" | "number" = "number",
): string {
    const compact = toCompactMagnitude(value);

    if (compact) {
        const sign = value < 0 ? "-" : "";
        return valueFormat === "currency" ? `${sign}$${compact}` : `${sign}${compact}`;
    }

    return valueFormat === "currency"
        ? currencyFormatter.format(value)
        : value.toLocaleString("en-US");
}

export function getNumberRangeDisplayText(
    values: readonly string[],
    valueFormat: "currency" | "number" = "number",
): string {
    const parsed = parseNumberRangeValue(values);
    if (!parsed) {
        return "";
    }

    return `${formatNumberRangeBound(parsed[0], valueFormat)} - ${formatNumberRangeBound(parsed[1], valueFormat)}`;
}
